import React, { useState, useEffect } from 'react';
import { Users, Home, Calendar, Star, AlertTriangle, DollarSign, Clock, CheckCircle, Search, Download, Plus, Phone, MessageSquare, Edit, Eye, Filter, UserPlus, Bell } from 'lucide-react';

const StaffServices = () => {
  const [selectedView, setSelectedView] = useState('week');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Statistiques principales
  const stats = {
    activeStaff: 47,
    occupiedVillas: 12,
    weeklyHours: 156,
    averageRating: 4.9,
    emergencies: 3,
    weeklyCost: 42000
  };

  // Planning de la semaine
  const weekSchedule = [
    { date: 11, day: 'Mercredi' },
    { date: 12, day: 'Jeudi' },
    { date: 13, day: 'Vendredi' },
    { date: 14, day: 'Samedi' },
    { date: 15, day: 'Dimanche' },
    { date: 16, day: 'Lundi' },
    { date: 17, day: 'Mardi' }
  ];

  // Propriétés avec personnel assigné
  const properties = [
    {
      id: 'azure-dreams',
      name: 'Villa Azure Dreams',
      location: 'St-Barthélemy',
      staff: [
        {
          id: 'marie-r',
          name: 'Marie Rodriguez',
          role: 'House Manager',
          schedule: '8h-18h',
          avatar: 'MR'
        },
        {
          id: 'jean-b',
          name: 'Jean-Baptiste',
          role: 'Chef Privé',
          schedule: '7h-14h & 18h-22h',
          avatar: 'JB'
        },
        {
          id: 'clara-l',
          name: 'Clara Lewis',
          role: 'Housekeeping',
          schedule: '7h-15h',
          team: 'Clara & Team',
          avatar: 'CL'
        },
        {
          id: 'james-w',
          name: 'James Wilson',
          role: 'Butler',
          schedule: '10h-22h',
          avatar: 'JW'
        },
        {
          id: 'michel-d',
          name: 'Michel Dubois',
          role: 'Chauffeur',
          schedule: 'Sur demande',
          avatar: 'MD'
        }
      ],
      specialEvent: {
        type: 'Événement spécial',
        details: 'Chef + Équipe',
        staff: 'Jean-Baptiste',
        service: '4 serveurs',
        time: '19h-01h'
      }
    },
    {
      id: 'paradiso',
      name: 'Villa Paradiso',
      location: 'St-Tropez',
      staff: [
        {
          id: 'sophie-m',
          name: 'Sophie Martin',
          role: 'Concierge',
          schedule: '9h-19h',
          avatar: 'SM'
        },
        {
          id: 'team-alpha',
          name: 'Team Alpha',
          role: 'Housekeeping',
          schedule: '8h-16h',
          avatar: 'TA'
        },
        {
          id: 'francois-l',
          name: 'François Leclerc',
          role: 'Chef Privé',
          schedule: '18h-23h',
          avatar: 'FL'
        },
        {
          id: 'aquaclean',
          name: 'AquaClean Pro',
          role: 'Pool Service',
          schedule: '8h-10h',
          avatar: 'AP'
        }
      ],
      urgent: 'À pourvoir'
    },
    {
      id: 'serenity',
      name: 'Yacht Serenity',
      location: 'Mykonos',
      staff: [
        {
          id: 'john-a',
          name: 'John Anderson',
          role: 'Capitaine',
          schedule: '24/7',
          avatar: 'JA'
        },
        {
          id: 'crew-3',
          name: '3 membres',
          role: 'Équipage',
          schedule: 'Rotation',
          avatar: '3M'
        },
        {
          id: 'marco-r',
          name: 'Marco Rossi',
          role: 'Chef Yacht',
          schedule: '6h-22h',
          avatar: 'MR'
        },
        {
          id: 'emma-w',
          name: 'Emma Wilson',
          role: 'Stewardess',
          schedule: '7h-23h',
          avatar: 'EW'
        },
        {
          id: 'tech-marine',
          name: 'Tech Marine',
          role: 'Mécanicien',
          schedule: 'Check technique',
          avatar: 'TM'
        }
      ]
    }
  ];

  // Personnel disponible
  const availableStaff = [
    {
      id: 'anna-l',
      name: 'Anna Lopez',
      role: 'Chef Privé',
      rating: 4.9,
      status: 'Disponible',
      avatar: 'AL'
    },
    {
      id: 'paul-d',
      name: 'Paul Durand',
      role: 'Chauffeur VIP',
      rating: 5.0,
      status: 'Disponible',
      avatar: 'PD'
    },
    {
      id: 'lisa-m',
      name: 'Lisa Martinez',
      role: 'Spa Thérapeute',
      rating: 4.8,
      status: 'Libre à 14h',
      avatar: 'LM'
    }
  ];

  // Demandes en attente
  const pendingRequests = [
    {
      id: 'req-001',
      type: 'Chef pour Dîner Privé',
      priority: 'Urgent',
      property: 'Villa Azure Dreams',
      date: '15 Sept',
      time: '20h',
      details: '12 personnes • Cuisine française',
      avatar: '👨‍🍳'
    },
    {
      id: 'req-002',
      type: 'Transfert Aéroport',
      priority: 'Demain',
      property: 'Villa Paradiso',
      date: '14h30',
      time: 'SBH',
      details: '4 passagers + bagages',
      avatar: '🚗'
    }
  ];

  // Évaluations récentes
  const recentReviews = [
    { name: 'Jean-Baptiste', role: 'Chef', rating: 5 },
    { name: 'Marie R.', role: 'Manager', rating: 5 },
    { name: 'Clara L.', role: 'Housekeeping', rating: 5 },
    { name: 'Michel D.', role: 'Chauffeur', rating: 5 }
  ];

  // Détail des paiements
  const weeklyPayments = {
    fixedSalaries: 28500,
    overtime: 4200,
    externalProviders: 8750,
    performanceBonus: 1500,
    total: 42950
  };

  const getStaffStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'disponible': return 'bg-green-100 text-green-800';
      case 'occupé': return 'bg-red-100 text-red-800';
      case 'libre à 14h': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'demain': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const PropertyCard = ({ property }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-800">{property.name}</h3>
          <p className="text-sm text-gray-600">{property.location}</p>
        </div>
        {property.urgent && (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
            ⚠️ {property.urgent}
          </span>
        )}
      </div>

      <div className="space-y-2">
        {property.staff.map((member) => (
          <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                {member.avatar}
              </div>
              <div>
                <div className="font-medium text-sm text-gray-800">{member.name}</div>
                <div className="text-xs text-gray-600">{member.role}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500 text-right">
              <div>{member.schedule}</div>
              {member.team && <div className="text-gray-400">{member.team}</div>}
            </div>
          </div>
        ))}

        {property.specialEvent && (
          <div className="border-t pt-2 mt-2">
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
              <div className="text-sm font-medium text-yellow-800">{property.specialEvent.type}</div>
              <div className="text-xs text-yellow-700">
                {property.specialEvent.details} • {property.specialEvent.service} • {property.specialEvent.time}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="staff-services">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Staff & Services Management</h1>
            <p className="text-gray-600 mt-1">Gestion du personnel et des services pour vos propriétés de luxe</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <UserPlus size={18} />
              <span>Ajouter Personnel</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Bell size={18} />
              <span>Nouvelle Demande</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
              <Calendar size={18} />
              <span>Planning Complet</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <AlertTriangle size={18} />
              <span>Urgence</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.activeStaff}</div>
            <div className="text-sm text-gray-600">Staff Actif</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Home className="text-green-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.occupiedVillas}</div>
            <div className="text-sm text-gray-600">Villas Occupées</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-purple-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.weeklyHours}</div>
            <div className="text-sm text-gray-600">Heures Cette Semaine</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-yellow-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.averageRating}</div>
            <div className="text-sm text-gray-600">Note Moyenne</div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.emergencies}</div>
            <div className="text-sm text-gray-600">Urgences</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-orange-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">€{stats.weeklyCost / 1000}K</div>
            <div className="text-sm text-gray-600">Coût Semaine</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Navigation du planning */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Planning Personnel</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedView('week')}
                className={`px-3 py-1 text-sm rounded ${selectedView === 'week' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Semaine
              </button>
              <button
                onClick={() => setSelectedView('day')}
                className={`px-3 py-1 text-sm rounded ${selectedView === 'day' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Jour
              </button>
              <button
                onClick={() => setSelectedView('month')}
                className={`px-3 py-1 text-sm rounded ${selectedView === 'month' ? 'bg-yellow-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Mois
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2 mb-4">
            <span className="text-sm font-medium text-gray-700">Propriétés</span>
            <div className="flex space-x-2">
              {weekSchedule.map((day) => (
                <div key={day.date} className="text-center">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-700 mb-1">
                    {day.date}
                  </div>
                  <div className="text-xs text-gray-600">{day.day}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Planning des propriétés */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-4">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>

          {/* Sidebar droite */}
          <div className="space-y-6">
            {/* Personnel disponible */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Personnel Disponible</h3>
                <span className="text-sm text-blue-600">Aujourd'hui</span>
              </div>
              
              <div className="space-y-3">
                {availableStaff.map((staff) => (
                  <div key={staff.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {staff.avatar}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{staff.name}</h4>
                      <p className="text-sm text-gray-600">{staff.role} • ⭐ {staff.rating}</p>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStaffStatusColor(staff.status)}`}>
                        {staff.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Demandes en attente */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Demandes en Attente</h3>
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold">3</span>
              </div>
              
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{request.avatar}</span>
                        <div>
                          <h4 className="font-medium text-gray-800">{request.type}</h4>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getPriorityColor(request.priority)}`}>
                            {request.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-3">
                      <div>{request.property} • {request.date} • {request.time}</div>
                      <div className="text-gray-500">{request.details}</div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button className="flex-1 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                        Assigner
                      </button>
                      <button className="flex-1 px-3 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50">
                        Refuser
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Évaluations récentes */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Évaluations Récentes</h3>
              
              <div className="space-y-3">
                {recentReviews.map((review, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-800">{review.name}</div>
                      <div className="text-sm text-gray-600">({review.role})</div>
                    </div>
                    <div className="flex items-center">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} size={14} className="text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Paiements semaine */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Paiements Semaine</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Salaires fixes</span>
                  <span className="font-medium">€{weeklyPayments.fixedSalaries.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Heures supplémentaires</span>
                  <span className="font-medium">€{weeklyPayments.overtime.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Prestataires externes</span>
                  <span className="font-medium">€{weeklyPayments.externalProviders.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Bonus performance</span>
                  <span className="font-medium">€{weeklyPayments.performanceBonus.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t pt-2 font-bold">
                  <span className="text-gray-800">TOTAL</span>
                  <span className="text-green-600">€{weeklyPayments.total.toLocaleString()}</span>
                </div>
              </div>
              
              <button className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                Valider les Paiements
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffServices;